import type { DeviceSync } from "@workspace/api-client-react";

/**
 * Offline window policy shared by every offline-capable player shell
 * (desktop, tv, mobile). A device caches its resolved schedule plus every
 * referenced media file so playback survives a network outage for a
 * configurable limited duration.
 *
 * The window is a per-device setting delivered inside `device.settings` on
 * `/device/sync`:
 *   - `offlineCacheHours`  — schedule + media look-ahead (default 7 days)
 *   - `offlineCacheMaxMb`  — media cache size cap for filesystem caches
 *
 * Everything here is pure so shells and the api-server can share the exact
 * clamping/collection semantics (and so it all runs under `node --test`).
 */

export const OFFLINE_WINDOW_DEFAULT_HOURS = 168;
export const OFFLINE_WINDOW_MIN_HOURS = 1;
export const OFFLINE_WINDOW_MAX_HOURS = 336;

export const OFFLINE_CACHE_DEFAULT_MB = 4096;
export const OFFLINE_CACHE_MIN_MB = 256;
export const OFFLINE_CACHE_MAX_MB = 65536;

/** Baseline sync look-ahead for online-only players (the server default). */
export const ONLINE_SYNC_HORIZON_MINUTES = 30;

function readIntSetting(settings: unknown, key: string): number | null {
  if (typeof settings !== "object" || settings === null) return null;
  const raw = (settings as Record<string, unknown>)[key];
  if (typeof raw !== "number" || !Number.isFinite(raw)) return null;
  return Math.trunc(raw);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Offline cache window in hours (clamped; default when unset/invalid). */
export function resolveOfflineWindowHours(settings: unknown): number {
  const raw = readIntSetting(settings, "offlineCacheHours");
  if (raw === null) return OFFLINE_WINDOW_DEFAULT_HOURS;
  return clamp(raw, OFFLINE_WINDOW_MIN_HOURS, OFFLINE_WINDOW_MAX_HOURS);
}

/** Media cache cap in bytes (clamped; default when unset/invalid). */
export function resolveOfflineCacheMaxBytes(settings: unknown): number {
  const raw = readIntSetting(settings, "offlineCacheMaxMb");
  const mb =
    raw === null
      ? OFFLINE_CACHE_DEFAULT_MB
      : clamp(raw, OFFLINE_CACHE_MIN_MB, OFFLINE_CACHE_MAX_MB);
  return mb * 1024 * 1024;
}

/**
 * The `?horizonMinutes=` an offline-capable shell passes to `/device/sync` so
 * the payload's timed events / boundaries cover its whole offline window.
 */
export function syncHorizonMinutes(settings: unknown): number {
  return resolveOfflineWindowHours(settings) * 60;
}

/**
 * Server-side clamp for a requested `?horizonMinutes=`. Absent/invalid keeps
 * the online default; requests never exceed the device's own offline window.
 */
export function clampSyncHorizonMinutes(
  requested: number | null | undefined,
  settings: unknown,
): number {
  if (
    requested === null ||
    requested === undefined ||
    !Number.isFinite(requested)
  ) {
    return ONLINE_SYNC_HORIZON_MINUTES;
  }
  const max = resolveOfflineWindowHours(settings) * 60;
  return clamp(Math.trunc(requested), ONLINE_SYNC_HORIZON_MINUTES, max);
}

/**
 * Stable cache key for a media item. Keyed by content identity — NOT the URL —
 * so cached files survive presigned-URL rotation across syncs.
 */
export function mediaCacheKey(item: {
  id: string;
  source: string;
  mediaAssetId?: string | null;
  catalogueTrackId?: string | null;
}): string {
  if (item.mediaAssetId) return `asset:${item.mediaAssetId}`;
  if (item.catalogueTrackId) return `catalogue:${item.catalogueTrackId}`;
  return `${item.source}:${item.id}`;
}

export type SyncMediaTarget = {
  key: string;
  url: string;
  kind: "media" | "artwork";
  /**
   * Earliest wall-clock time the item is needed (ISO), or null when it is part
   * of the base program and needed immediately. Used to order prefetch and to
   * pick eviction victims when required media alone exceeds the cache cap.
   */
  firstNeededAt: string | null;
};

/**
 * Every media file referenced by a sync payload, deduped by cache key and
 * ordered by need (base program first, then timed events by fire time).
 * Mirrors the web player's `mediaUrls()` coverage: playlists + timed events
 * (playlist or direct media asset) + schedule boundaries, including artwork.
 */
export function collectSyncMedia(sync: DeviceSync): SyncMediaTarget[] {
  // Playlists referenced by the base program (now/next slots + boundaries)
  // are needed immediately; playlists referenced only by timed events are
  // needed at their earliest fire time.
  const eventPlaylistFirstFire = new Map<string, string>();
  const eventAssetFirstFire = new Map<string, string>();
  for (const event of sync.timedEvents ?? []) {
    if (event.playlistId) {
      const prev = eventPlaylistFirstFire.get(event.playlistId);
      if (!prev || event.fireAt < prev) {
        eventPlaylistFirstFire.set(event.playlistId, event.fireAt);
      }
    }
    if (event.mediaAssetId) {
      const prev = eventAssetFirstFire.get(event.mediaAssetId);
      if (!prev || event.fireAt < prev) {
        eventAssetFirstFire.set(event.mediaAssetId, event.fireAt);
      }
    }
  }

  const basePlaylistIds = new Set<string>();
  for (const channel of sync.plan?.channels ?? []) {
    for (const slot of [channel.now, channel.next]) {
      if (slot?.playlistId) basePlaylistIds.add(slot.playlistId);
    }
  }
  for (const boundary of sync.boundaries ?? []) {
    if (boundary.sourceId) basePlaylistIds.add(boundary.sourceId);
  }

  const byKey = new Map<string, SyncMediaTarget>();
  const note = (
    key: string,
    url: string | null | undefined,
    kind: "media" | "artwork",
    firstNeededAt: string | null,
  ) => {
    if (!url) return;
    const existing = byKey.get(key);
    if (existing) {
      // Needed-now (null) wins; otherwise keep the earliest fire time.
      if (existing.firstNeededAt === null) return;
      if (firstNeededAt === null || firstNeededAt < existing.firstNeededAt) {
        existing.firstNeededAt = firstNeededAt;
      }
      return;
    }
    byKey.set(key, { key, url, kind, firstNeededAt });
  };

  for (const playlist of sync.playlists ?? []) {
    const isBase = basePlaylistIds.has(playlist.id);
    for (const item of playlist.items) {
      const key = mediaCacheKey(item);
      let firstNeededAt: string | null = null;
      if (!isBase) {
        firstNeededAt =
          eventPlaylistFirstFire.get(playlist.id) ??
          (item.mediaAssetId
            ? (eventAssetFirstFire.get(item.mediaAssetId) ?? null)
            : null);
      }
      note(key, item.url, "media", firstNeededAt);
      note(`${key}:artwork`, item.artworkUrl, "artwork", firstNeededAt);
    }
  }

  return [...byKey.values()].sort((a, b) => {
    if (a.firstNeededAt === b.firstNeededAt) return a.key < b.key ? -1 : 1;
    if (a.firstNeededAt === null) return -1;
    if (b.firstNeededAt === null) return 1;
    return a.firstNeededAt < b.firstNeededAt ? -1 : 1;
  });
}

export type CacheIndexEntry = {
  key: string;
  bytes: number;
  /** Last written/used timestamp (ms epoch) — the LRU axis. */
  cachedAt: number;
};

/**
 * Pick eviction victims to bring the cache under `maxBytes`.
 *
 * Non-required entries go first (LRU by `cachedAt`). If required media alone
 * still exceeds the cap, required entries are evicted from the END of
 * `requiredKeysInNeedOrder` (the latest-needed content) — those items fall
 * back to streaming when the device is online again.
 */
export function planEviction(
  index: CacheIndexEntry[],
  maxBytes: number,
  requiredKeysInNeedOrder: readonly string[],
): string[] {
  let total = index.reduce((sum, e) => sum + e.bytes, 0);
  if (total <= maxBytes) return [];

  const required = new Set(requiredKeysInNeedOrder);
  const bytesByKey = new Map(index.map((e) => [e.key, e.bytes]));
  const victims: string[] = [];

  const optional = index
    .filter((e) => !required.has(e.key))
    .sort((a, b) => a.cachedAt - b.cachedAt);
  for (const entry of optional) {
    if (total <= maxBytes) break;
    victims.push(entry.key);
    total -= entry.bytes;
  }

  if (total > maxBytes) {
    for (let i = requiredKeysInNeedOrder.length - 1; i >= 0; i--) {
      if (total <= maxBytes) break;
      const key = requiredKeysInNeedOrder[i];
      const bytes = bytesByKey.get(key);
      if (bytes === undefined) continue;
      victims.push(key);
      total -= bytes;
    }
  }

  return victims;
}

// ---------------------------------------------------------------------------
// Offline readiness
// ---------------------------------------------------------------------------

/**
 * Coarse state a console can badge without knowing the numbers.
 *
 * `empty` is not a failure: nothing is scheduled for this device. `incomplete`
 * is the alarm, because the program that would play RIGHT NOW has gaps; a
 * device that is merely still filling its look-ahead is `caching` and will
 * survive an outage starting this second.
 */
export type OfflineReadinessState = "ready" | "caching" | "incomplete" | "empty";

/**
 * A file the readiness pass looks for.
 *
 * `SyncMediaTarget` already satisfies this, so native passes
 * `collectSyncMedia(sync)` straight through. Web keys its Cache Storage by
 * query-stripped URL rather than by content id, so it re-keys first (see
 * `rekeySyncMedia`) and carries the content identity in `id`, which keeps URLs
 * — and their signatures — off the wire.
 */
export interface ReadinessTarget {
  /** Key the LOCAL cache stores this file under. */
  key: string;
  /** Stable content identity for reporting. Defaults to `key`. */
  id?: string;
  kind: "media" | "artwork";
  firstNeededAt: string | null;
}

export interface OfflineReadinessMissing {
  id: string;
  kind: "media" | "artwork";
  firstNeededAt: string | null;
}

export interface OfflineReadinessReport {
  /** Payload version, so an older row still renders honestly. */
  v: 1;
  state: OfflineReadinessState;

  neededCount: number;
  cachedCount: number;
  /** firstNeededAt === null: what an outage starting NOW needs. */
  baseNeededCount: number;
  baseCachedCount: number;
  /** Missing artwork is cosmetic; missing media is silence in a venue. */
  mediaNeededCount: number;
  mediaCachedCount: number;
  artworkNeededCount: number;
  artworkCachedCount: number;

  /** Bytes held for the needed set. */
  cachedBytes: number;
  /** Bytes of the WHOLE local index, including leftovers from an older window. */
  totalBytes: number;
  /** resolveOfflineCacheMaxBytes(device.settings). */
  maxBytes: number;
  overCap: boolean;

  missingCount: number;
  /** Capped sample, base-first then by need time. Never the full list. */
  missing: OfflineReadinessMissing[];

  /** The sync this describes, so a stale report is recognisable. */
  syncVersion: string | null;
  /** Device wall clock at computation (ms epoch). */
  at: number;
}

/** Wire cap on the missing sample. Counts carry the magnitude. */
export const READINESS_MISSING_LIMIT = 20;

/**
 * Re-key targets for a cache that does not use `mediaCacheKey`.
 *
 * Web/desktop store Cache Storage entries under the query-stripped URL, so the
 * same file has a different local key there. Dedupes with the same rule as
 * `collectSyncMedia`: needed-now wins, otherwise the earliest fire time.
 */
export function rekeySyncMedia(
  targets: readonly SyncMediaTarget[],
  keyOf: (target: SyncMediaTarget) => string,
): ReadinessTarget[] {
  const byKey = new Map<string, ReadinessTarget>();
  for (const target of targets) {
    const key = keyOf(target);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, {
        key,
        id: target.key,
        kind: target.kind,
        firstNeededAt: target.firstNeededAt,
      });
      continue;
    }
    if (existing.firstNeededAt === null) continue;
    if (
      target.firstNeededAt === null ||
      target.firstNeededAt < existing.firstNeededAt
    ) {
      existing.firstNeededAt = target.firstNeededAt;
    }
  }
  return [...byKey.values()];
}

/**
 * How much of this device's own sync window is on local disk.
 *
 * Deliberately takes targets + index + cap rather than a DeviceSync, so one
 * implementation serves both cache layouts. Pure: no filesystem, no IndexedDB,
 * no clock unless one is passed.
 */
export function computeOfflineReadiness(input: {
  targets: readonly ReadinessTarget[];
  index: readonly CacheIndexEntry[];
  maxBytes: number;
  syncVersion?: string | null;
  now?: number;
  missingLimit?: number;
}): OfflineReadinessReport {
  const { targets, index, maxBytes } = input;
  const limit = input.missingLimit ?? READINESS_MISSING_LIMIT;

  const byKey = new Map<string, CacheIndexEntry>();
  let totalBytes = 0;
  for (const entry of index) {
    byKey.set(entry.key, entry);
    totalBytes += entry.bytes;
  }

  let cachedCount = 0;
  let baseNeededCount = 0;
  let baseCachedCount = 0;
  let mediaNeededCount = 0;
  let mediaCachedCount = 0;
  let artworkNeededCount = 0;
  let artworkCachedCount = 0;
  let cachedBytes = 0;
  const missing: OfflineReadinessMissing[] = [];

  // Base-first, then by need time, so the capped sample shows what matters.
  const ordered = [...targets].sort((a, b) => {
    if (a.firstNeededAt === b.firstNeededAt) return a.key < b.key ? -1 : 1;
    if (a.firstNeededAt === null) return -1;
    if (b.firstNeededAt === null) return 1;
    return a.firstNeededAt < b.firstNeededAt ? -1 : 1;
  });

  for (const target of ordered) {
    const entry = byKey.get(target.key);
    const isBase = target.firstNeededAt === null;
    const isMedia = target.kind === "media";

    if (isBase) baseNeededCount += 1;
    if (isMedia) mediaNeededCount += 1;
    else artworkNeededCount += 1;

    if (entry) {
      cachedCount += 1;
      cachedBytes += entry.bytes;
      if (isBase) baseCachedCount += 1;
      if (isMedia) mediaCachedCount += 1;
      else artworkCachedCount += 1;
    } else {
      missing.push({
        id: target.id ?? target.key,
        kind: target.kind,
        firstNeededAt: target.firstNeededAt,
      });
    }
  }

  const neededCount = ordered.length;
  const missingCount = missing.length;

  let state: OfflineReadinessState;
  if (neededCount === 0) state = "empty";
  else if (missingCount === 0) state = "ready";
  else if (baseCachedCount === baseNeededCount) state = "caching";
  else state = "incomplete";

  return {
    v: 1,
    state,
    neededCount,
    cachedCount,
    baseNeededCount,
    baseCachedCount,
    mediaNeededCount,
    mediaCachedCount,
    artworkNeededCount,
    artworkCachedCount,
    cachedBytes,
    totalBytes,
    maxBytes,
    // Reported, not acted on: eviction is planEviction's job and has already run.
    overCap: totalBytes > maxBytes,
    missingCount,
    missing: missing.slice(0, limit),
    syncVersion: input.syncVersion ?? null,
    at: input.now ?? Date.now(),
  };
}
